import { motion } from "framer-motion";
import {
  BookOpen,
  BarChart3,
  CheckCircle2,
  GraduationCap,
  Medal,
  TrendingUp,
  Users,
} from "lucide-react";

import { Card, CardHeader, Row } from "@/components/Card";
import StatCard from "@/components/StatCard";
import { useApp } from "@/App";
import type { Role } from "@/data/mock";

type AcademicMetric = {
  label: string;
  value: string;
  caption: string;
  tone: "blue" | "green" | "orange" | "red" | "purple" | "brand";
  icon: typeof BookOpen;
  spark?: number[];
};

const METRICS: Record<Exclude<Role, "parent"> | "parent", AcademicMetric[]> = {
  director: [
    { label: "Avg. score", value: "68%", caption: "Across all classes", tone: "brand", icon: TrendingUp, spark: [62, 64, 63, 66, 65, 67, 68] },
    { label: "Mark completion", value: "86%", caption: "Term 2 assessments", tone: "green", icon: CheckCircle2, spark: [58, 66, 68, 73, 77, 82, 86] },
    { label: "Top stream", value: "S3 Blue", caption: "74% average", tone: "orange", icon: Medal },
    { label: "At risk", value: "12", caption: "Need intervention", tone: "red", icon: Users },
  ],
  bursar: [
    { label: "Avg. score", value: "68%", caption: "School average", tone: "brand", icon: TrendingUp },
    { label: "Completion", value: "86%", caption: "Records ready", tone: "green", icon: CheckCircle2 },
    { label: "Top stream", value: "S3 Blue", caption: "74% average", tone: "orange", icon: Medal },
    { label: "At risk", value: "12", caption: "Awaiting follow-up", tone: "red", icon: Users },
  ],
  teacher: [
    { label: "My average", value: "71%", caption: "Across 5 streams", tone: "brand", icon: TrendingUp, spark: [66, 67, 68, 69, 70, 70, 71] },
    { label: "Marks ready", value: "86%", caption: "23 still pending", tone: "green", icon: CheckCircle2 },
    { label: "Subjects", value: "3", caption: "This term", tone: "orange", icon: BookOpen },
    { label: "Students", value: "168", caption: "Across 5 streams", tone: "blue", icon: Users },
  ],
  student: [
    { label: "My average", value: "74%", caption: "Term 2 so far", tone: "brand", icon: TrendingUp, spark: [68, 70, 69, 72, 71, 73, 74] },
    { label: "Subjects", value: "8", caption: "This term", tone: "blue", icon: BookOpen },
    { label: "Completed", value: "12", caption: "Of 16 assessments", tone: "green", icon: CheckCircle2 },
    { label: "Next exam", value: "6 days", caption: "Mathematics", tone: "orange", icon: GraduationCap },
  ],
  parent: [
    { label: "Children average", value: "74%", caption: "Term 2 so far", tone: "brand", icon: TrendingUp },
    { label: "Subjects", value: "8", caption: "Across your children", tone: "blue", icon: BookOpen },
    { label: "Completed", value: "12", caption: "Of 16 assessments", tone: "green", icon: CheckCircle2 },
    { label: "Next exam", value: "6 days", caption: "Mathematics", tone: "orange", icon: GraduationCap },
  ],
};

const ROLE_COPY: Record<Role, { eyebrow: string; title: string; subtitle: string }> = {
  director: { eyebrow: "School performance", title: "Academic overview", subtitle: "A live view of learning progress across the school." },
  bursar: { eyebrow: "Operations view", title: "Academic pulse", subtitle: "Keep learning outcomes visible beside the finance desk." },
  teacher: { eyebrow: "Your classrooms", title: "Teaching dashboard", subtitle: "Track marks, class progress, and students who need support." },
  student: { eyebrow: "Your learning", title: "Academic dashboard", subtitle: "Stay close to your progress and upcoming assessments." },
  parent: { eyebrow: "Family learning", title: "Academic dashboard", subtitle: "See how your children are progressing this term." },
};

type AcademicRow = { title: string; subtitle: string; value: string; tone: "blue" | "green" | "orange" | "red" | "purple" | "brand" };

const ROLE_ROWS: Record<Role, AcademicRow[]> = {
  director: [
    { title: "S3 Blue", subtitle: "Mathematics · 32 students", value: "74%", tone: "brand" },
    { title: "S4 Red", subtitle: "Physics · 28 students", value: "71%", tone: "blue" },
  ],
  bursar: [
    { title: "Fee-linked progress", subtitle: "Students with cleared balances", value: "82%", tone: "green" },
    { title: "Accounts needing review", subtitle: "Students with payment and mark gaps", value: "18", tone: "orange" },
  ],
  teacher: [
    { title: "S3 Blue", subtitle: "Mathematics · 32 students", value: "74%", tone: "brand" },
    { title: "S2 Green", subtitle: "Mathematics · 35 students", value: "69%", tone: "blue" },
  ],
  student: [
    { title: "Mathematics", subtitle: "Next assessment in 6 days", value: "74%", tone: "brand" },
    { title: "Physics", subtitle: "Latest mark published", value: "78%", tone: "green" },
  ],
  parent: [
    { title: "Amina Nakato", subtitle: "S3 Blue · 8 subjects", value: "76%", tone: "brand" },
    { title: "Joel Wanyama", subtitle: "S1 Green · 8 subjects", value: "71%", tone: "green" },
  ],
};

export default function Academics() {
  const role = useApp((s) => s.role) ?? "director";
  const copy = ROLE_COPY[role];
  const metrics = METRICS[role];
  const rows = ROLE_ROWS[role];

  return (
    <div className="px-5 pb-8 pt-5">
      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <p className="text-ios-footnote text-ios-secondary">{copy.eyebrow} · Term 2, 2026</p>
        <h1 className="mt-0.5 text-ios-title-1 font-bold text-ios-label">{copy.title}</h1>
        <p className="mt-1 max-w-sm text-ios-caption-1 text-ios-secondary">{copy.subtitle}</p>
      </motion.div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        {metrics.map((metric, index) => <StatCard key={metric.label} {...metric} index={index} />)}
      </div>

      <Card className="mt-5" index={4}>
        <CardHeader title={role === "student" || role === "parent" ? "My learning" : "Learning priorities"} subtitle={role === "director" ? "School-wide performance" : "Scoped to your role"} />
        <div>
          {rows.map((row, index) => <Row key={row.title} title={row.title} subtitle={row.subtitle} leading={index === 0 ? CheckCircle2 : BarChart3} leadingTone={row.tone} trailing={row.value} chevron separator={index < rows.length - 1} />)}
        </div>
      </Card>
    </div>
  );
}
